import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useServices} from '@/services/context'
import type {
    ServiceAccount,
    ServiceAccountCreateRequest,
    ServiceAccountUpdateRequest,
    ApiKey,
    ApiKeyCreateRequest,
} from '@/types/service-account'

export const SERVICE_ACCOUNTS_KEY = 'service-accounts'
export const SERVICE_ACCOUNT_KEY = 'service-account'
export const API_KEYS_KEY = 'api-keys'

export function useServiceAccount(id: string | undefined) {
    const services = useServices()
    return useQuery<ServiceAccount>({
        queryKey: [SERVICE_ACCOUNT_KEY, id],
        queryFn: () => services.serviceAccounts.detail(id!),
        enabled: !!id,
    })
}

export function useApiKeys(serviceAccountId: string | undefined) {
    const services = useServices()
    return useQuery<ApiKey[]>({
        queryKey: [SERVICE_ACCOUNT_KEY, serviceAccountId, API_KEYS_KEY],
        queryFn: () => services.apiKeys.list(serviceAccountId!),
        enabled: !!serviceAccountId,
    })
}

export function useCreateServiceAccount() {
    const services = useServices()
    return useEntityMutation<ServiceAccount, Error, ServiceAccountCreateRequest>({
        mutationFn: (req: ServiceAccountCreateRequest) => services.serviceAccounts.create(req),
        invalidate: [SERVICE_ACCOUNTS_KEY],
    })
}

export function useUpdateServiceAccount(id: string) {
    const services = useServices()
    const queryClient = useQueryClient()
    return useEntityMutation<ServiceAccount, Error, ServiceAccountUpdateRequest>({
        mutationFn: (req: ServiceAccountUpdateRequest) => services.serviceAccounts.update(id, req),
        invalidate: [[SERVICE_ACCOUNTS_KEY], [SERVICE_ACCOUNT_KEY, id]],
        onSuccess: (data: ServiceAccount) => {
            queryClient.setQueryData([SERVICE_ACCOUNT_KEY, id], data)
        },
    })
}

export function useDeactivateServiceAccount(id: string) {
    const services = useServices()
    return useEntityMutation<ServiceAccount, Error, void>({
        mutationFn: () => services.serviceAccounts.deactivate(id),
        invalidate: [
            [SERVICE_ACCOUNTS_KEY],
            [SERVICE_ACCOUNT_KEY, id],
            [SERVICE_ACCOUNT_KEY, id, API_KEYS_KEY],
        ],
    })
}

export function useMintApiKey(serviceAccountId: string) {
    const services = useServices()
    return useEntityMutation({
        mutationFn: (req: ApiKeyCreateRequest) => services.apiKeys.mint(serviceAccountId, req),
        invalidate: [
            [SERVICE_ACCOUNT_KEY, serviceAccountId],
            [SERVICE_ACCOUNT_KEY, serviceAccountId, API_KEYS_KEY],
            [SERVICE_ACCOUNTS_KEY],
        ],
        // WHY: a retried mint creates a SECOND key on the server (per spec §4
        // rule S2 — there is no idempotency key on the mint endpoint). Disable
        // retries so a transient 5xx surfaces as a single error toast.
        retry: false,
    })
}

export function useRevokeApiKey(serviceAccountId: string) {
    const services = useServices()
    return useEntityMutation({
        mutationFn: (keyId: string) => services.apiKeys.revoke(serviceAccountId, keyId),
        invalidate: [
            [SERVICE_ACCOUNT_KEY, serviceAccountId],
            [SERVICE_ACCOUNT_KEY, serviceAccountId, API_KEYS_KEY],
            [SERVICE_ACCOUNTS_KEY],
        ],
    })
}
